// Core
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {NavLink, useLocation} from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import {
  AbsoluteCenter,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Image,
  IconButton,
  Link,
  Text,
  Select,
  Checkbox, 
  CheckboxGroup,
  Stack,
  useDisclosure,
  useColorModeValue,
  useColorMode,
  Spinner
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { validateEmail, isValidPassword } from 'services/Utils';
import logo from 'assets/img/logo.png';

// Components
import { StarsIcon, EmailIcon, PhoneIcon } from 'components/Icons/Icons';
import EditField from 'components/Form/EditField';
import ErrorCard from 'components/Form/Errors';
import FooterAuth from 'components/Footer/Auth';

function SignUpSetPassword() {
  const textColor = useColorModeValue('#0E1633', 'white');
  const textSigninColor = useColorModeValue('#0E1633', '#9FABCB');
  const borderColor = useColorModeValue('gray.200');
  const topLineDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 56.18%, #FFF 68.16%, #38A9E1 82.69%, rgba(56, 169, 225, 0.00) 100%)';
  const topLineWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const topLine = useColorModeValue(topLineWhite, topLineDark);
  const errorLine = 'linear-gradient(90deg, rgba(253, 43, 42, 0.00) 9.63%, #FD2B2A 37.41%, #F88 50%, #FD2B2A 60.02%, rgba(253, 43, 42, 0.00) 91.14%)';
  
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [showPass, setShowPass] = React.useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const handlePassClick = () => setShowPass(!showPass);
  const [showConfPass, setShowConfPass] = React.useState(false);
  const handleConfPassClick = () => setShowConfPass(!showConfPass);

  const [newObject, setNewObject] = useState({});
  const [newObjectErrors, setNewObjectErrors] = useState([]);
  const [errorCard, setErrorCard] = useState();
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [buttonContinueDisable, setButtonContinueDisable] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [inputPhoneLoading, setInputPhoneLoading] = useState(false);

  useMemo(() => {
    setErrorCard();
  }, [newObject]);

  const location = useLocation();

  useEffect(() => {
	const params = new URLSearchParams(location.search);

	const hash = params.get('hash');

	if (!hash) {
	    history.push('/auth/login');
	    return;
	}

  let email = null;
  let cid = null;

  try {
    const decoded = JSON.parse(atob(hash));

    email = decoded.email;
    cid = decoded.cid;
  } catch (e) {
    history.push('/auth/login');
    return;
  }

	setNewObject((prev) => ({
		...prev,
	    email,
	    cid,
	}));

	checkRegistration(email, cid);
  }, []);

  const checkRegistration = async (email, cid) => {
	try {
	    const res = await apiService.registrationCheckPassword({
	      email,
	      cid,
	    });

      if(res.statusCode === 200 && res?.data === true || res.statusCode !== 200){
         history.push('/download');
      }

      setIsLoadingPage(false);
	} catch (e) {
	    history.push('/auth/login');
	}
  };

  const isValidInput = useMemo(() => {
    const fields = [
      'cid',
      'email',
      'password',
      'confirmPassword',
    ];

    return fields.reduce((acc, field) => {
      acc[field] = !newObjectErrors.includes(field);
      return acc;
    }, {});
  }, [newObjectErrors]);

  const setData = useCallback(
    (maxValue, target, key) => {
      if (target.length < maxValue) {
        setNewObjectErrors([...newObjectErrors, key]);
      } else {
        setNewObjectErrors(newObjectErrors.filter((error) => error !== key));
      }
      setNewObject({ ...newObject, [key]: target });
    },
    [newObject, newObjectErrors, setNewObject, setNewObjectErrors],
  );

  const handleField = useMemo(() => {
    const fieldConfigs = [
      { field: 'cid', maxValue: 0 }, 		
      { field: 'email', maxValue: 1 },
      { field: 'password', maxValue: 1 },
      { field: 'confirmPassword', maxValue: 1 },
    ];

    return fieldConfigs.reduce((acc, { field, maxValue }) => {
      acc[field] = (e) => setData(maxValue, e.target.value, field);
      return acc;
    }, {});
  }, [setData]);

  useEffect(() => {
    if (
	  newObject.email &&
	  newObject.password &&
	  newObject.confirmPassword &&
	  newObject.cid
	) {
	  setButtonContinueDisable(false);
	} else {
	  setButtonContinueDisable(true);
	}
  }, [newObject, handleField]);

  const history = useHistory();
  const handleCreateObject = useCallback(async () => {
    setIsLoadingButton(true);
    
    const errors = [];
    if (!newObject.cid) {
	  errors.push('cid');
	}

    if (!newObject.email || !validateEmail(newObject.email)) {
      errors.push('email');
    }

    if (!newObject.password || newObject.password.length < 8 || !isValidPassword(newObject.password)) {
      errors.push('password');
    }

    if (newObject.password !== newObject.confirmPassword) {
      errors.push('confirmPassword');
    }

    setNewObjectErrors(errors);

    if (errors.length > 0) {
      setButtonContinueDisable(false);
      setIsLoadingButton(false);
      return;
    }

    try {
      const response = await apiService.registrationSetPassword({
        ...newObject
      });
      if (response.data === false || response.statusCode !== 200) {
        setErrorCard(response.message);
        setButtonContinueDisable(false);
        setIsLoadingButton(false);
        return;
      }
    } catch (e) {
      setButtonContinueDisable(false);
      setIsLoadingButton(false);
      return;
    }

    setButtonContinueDisable(false);
    setIsLoadingButton(false);
    history.push('/download?set-password=true');
    return;
  }, [history, newObject, newObjectErrors]);

  return (
    <Box 
      w='100%' 
      textAlign="center" 
      overflow="scroll" 
      sx={{
        scrollbarWidth: 'none', 
        msOverflowStyle: 'none', 
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
      h="100%" 
      py="80px">
      <Box mb={{base: '50px', sm: '60px', md: '79px'}} display="flex" alignItems="center" justifyContent="center">
        <NavLink to="/"><Image src={logo} alt='lmctplus image' maxWidth="184px"/></NavLink>
        { isDark && <Box bg="rgba(64, 155, 255, 0.82)" zIndex="-1" position="absolute" opacity="0.6" filter="blur(70px)" h="300px" w="200px" mt="10%" borderRadius="full" /> }
      </Box>

      <Box position="relative">
        <Text as="h1" variant="header-gradient" 
          fontSize={{base: '32px', sm: '40px', md: '48px'}}
          >
          Welcome to LMCT+</Text>
        <Box bg={topLine} left="50%" transform="translateX(-50%) rotate(180deg)" bottom="-2px" h="1px" position="absolute" w="50%" />
      </Box>

      <Flex 
          display="inline-flex"
          gap="8px"
          mt="8px"
          alignItems="center">
          <StarsIcon fontSize="24px" />
          <Box borderRadius={{base: '8px', md: '16px'}} p="4px 14px 4px 16px" background="white">
            <Text as="h2" variant="header-gradient" fontSize={{base: '26px', sm: '32px', md: '40px'}} pr="3px">Biggest & best</Text>
          </Box>
          <StarsIcon fontSize="24px" />
      </Flex>
      <Text fontSize={{base: '16px', md: '20px'}} color={textColor} mt="14px">Rewards Club in Australia</Text>

      
      <Box maxW={{sm: '100%', md: '81%'}} m="0 auto" pt={{base: '24px', md: '48px'}}>
      { isLoadingPage ? <Spinner /> :
      <>
	    { errorCard &&
		      <ErrorCard error={errorCard} maxW="100%" mb="16px"/>
		  }
        <FormControl mt="16px" id="email">
          <Input
			  borderColor={borderColor}
			  type="email"
			  value={newObject?.email || ''}
			  isDisabled
			/>
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.email ? '1' : '0'} />
        </FormControl>

        <FormControl id="password" mt="16px">
          <InputGroup>
            <Input
              borderColor={borderColor}
              placeholder='Password'
              autoComplete="off"
              value={newObject?.password}
              isValid={isValidInput.password}
              onChange={handleField.password}
              type={showPass ? 'text' : 'password'}
            />
            <InputRightElement>
              <IconButton
                aria-label={showPass ? 'Hide password' : 'Show password'}
                icon={showPass ?  <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                onClick={handlePassClick}
                mt='14px'
                mr='14px'
                color='gray.200'
                fontSize='24px'
                variant='ghost-md'
              />
            </InputRightElement>
          </InputGroup>
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.password || !isValidInput.password ? '1' : '0'} />
        </FormControl>

        <FormControl id="password" mt="16px">
          <InputGroup>
            <Input
              borderColor={borderColor}
              placeholder='Confirm Password'
              autoComplete="off"
              value={newObject?.confirmPassword}
              isValid={isValidInput.confirmPassword}
              onChange={handleField.confirmPassword}
              type={showConfPass ? 'text' : 'password'}
            />
            <InputRightElement>
              <IconButton
                aria-label={showConfPass ? 'Hide password' : 'Show password'}
                icon={showConfPass ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                onClick={handleConfPassClick}
                mt='14px'
                mr='14px'
                color='gray.200'
                fontSize='24px'
                variant='ghost-md'
              />
            </InputRightElement>
          </InputGroup>
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.confirmPassword || !isValidInput.confirmPassword ? '1' : '0'} />
        </FormControl>

        <Text variant="text-sm" color={textSigninColor} p={{base: '8px 0px 0 0px' ,lg: '8px 20px 0 20px'}}>
          Your password must be at least 8 characters long, contain 1 letter, 1 uppercase letter and 1 numder
        </Text>

        <Flex justifyContent="start" w="100%" gap="12px" my="22px">
          <Stack spacing={5} direction='row' alignItems="center">
              <Checkbox onChange={() => setAgreeTerms(!agreeTerms)}></Checkbox>
          </Stack>
          <Flex justifyContent="start" w="100%" gap="4px">
            <Text variant="text-md" color={textSigninColor}>I agree to the</Text>
            <Link href="/terms-and-conditions"><Text color="#099BFF">Terms and Conditions</Text></Link>
          </Flex>
        </Flex>

        <Button
          variant="button-md"
          loadingText="Log In"
          isLoading={isLoadingButton}
          disabled={isLoadingButton || (agreeTerms || buttonContinueDisable)}
          onClick={handleCreateObject}
          maxW={{base: '100%', sm: '168px'}}
          m="16px auto">Continue</Button>

        </>}

        <FooterAuth isDark={isDark} />
      </Box>
    </Box>
  );
}

export default SignUpSetPassword;